import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EcondosQuery } from '@api/model/query';
import { Subject } from 'rxjs';
import { takeUntil, timeout } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CondoTypeLabel,
  NotificationChannel,
  NotificationChannelLabelMap,
  SystemBroadcast,
  UserTypeLabel
} from '@api/model/system-broadcast';
import { SystemBroadcastService } from '@api/service/system-broadcast.service';
import { WhiteLabel } from '@api/model/white-label';
import { Condo } from '@api/model/condo';
import { DomSanitizer } from '@angular/platform-browser';
import { WhiteLabelService } from '@api/service/white-label.service';
import { endOfDay, startOfDay } from 'date-fns';
import { CondoService } from '@api/service/condo.service';
import { conditionalValidator } from '@api/util/validators';
import { beforeDateValidator } from '../../../../../validators/before-date.validator';
import { HugerteEditorComponent } from 'src/app/components/hugerte-editor/hugerte-editor.component';

type Status = 'LOADING' | 'SUCCESS' | 'ERROR';

@Component({
  selector: 'app-create-system-broadcast-page',
  templateUrl: './create-system-broadcast-page.component.html',
  styleUrls: ['./create-system-broadcast-page.component.scss']
})
export class CreateSystemBroadcastPageComponent implements OnInit, OnDestroy {
  @ViewChild('editor') editor!: HugerteEditorComponent;
  systemBroadcastId: SystemBroadcast['_id'] | null = null;
  systemBroadcast: SystemBroadcast | null = null;

  fetchSystemBroadcastQuery: EcondosQuery = {
    $select:
      'title message createdAt isActive htmlMessage whiteLabels userTypes condoTypes link linkButtonName startDate endDate linkButtonClickCount condos notificationChannel'
  };

  fetchStatus: Status | null = null;
  submitFormStatus: Status | null = null;

  systemBroadcastForm = new UntypedFormGroup({
    title: new UntypedFormControl('', Validators.required),
    htmlMessage: new UntypedFormControl('', Validators.required),
    isActive: new UntypedFormControl(false),
    link: new UntypedFormControl(''),
    linkButtonName: new UntypedFormControl(''),
    whiteLabels: new UntypedFormControl({ value: [], disabled: this.fetchStatus !== 'SUCCESS' }),
    userTypes: new UntypedFormControl({ value: [], disabled: this.fetchStatus !== 'SUCCESS' }),
    notificationChannel: new UntypedFormControl(NotificationChannel.WEB),
    condoTypes: new UntypedFormControl({ value: [], disabled: this.fetchStatus !== 'SUCCESS' }),
    startDate: new UntypedFormControl('', Validators.required),
    endDate: new UntypedFormControl(
      '',
      conditionalValidator(() => !!this.endDate.value, beforeDateValidator('startDate'))
    ),
    condos: new UntypedFormControl([])
  });

  title = this.systemBroadcastForm.get('title') as UntypedFormControl;
  htmlMessage = this.systemBroadcastForm.get('htmlMessage') as UntypedFormControl;
  isActive = this.systemBroadcastForm.get('isActive') as UntypedFormControl;
  link = this.systemBroadcastForm.get('link') as UntypedFormControl;
  linkButtonName = this.systemBroadcastForm.get('linkButtonName') as UntypedFormControl;
  whiteLabels = this.systemBroadcastForm.get('whiteLabels') as UntypedFormControl;
  userTypes = this.systemBroadcastForm.get('userTypes') as UntypedFormControl;
  notificationChannel = this.systemBroadcastForm.get('notificationChannel') as UntypedFormControl;
  condoTypes = this.systemBroadcastForm.get('condoTypes') as UntypedFormControl;
  startDate = this.systemBroadcastForm.get('startDate') as UntypedFormControl;
  endDate = this.systemBroadcastForm.get('endDate') as UntypedFormControl;
  condos = this.systemBroadcastForm.get('condos') as UntypedFormControl;

  notificationChannels = Object.values(NotificationChannel);
  notificationChannelLabelMap = NotificationChannelLabelMap;

  unsubscribe$ = new Subject();

  hugerteBaseConfig = {
    menubar: false,
    branding: false,
    statusbar: false,
    language: 'pt_BR',
    height: '200',
    plugins: 'textcolor colorpicker link lists advlist preview',
    toolbar:
      'undo redo | formatselect| bold italic underline | forecolor backcolor | fontsizeselect | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link',
    resize: true
  };

  systemWhiteLabels: WhiteLabel[] = [];
  systemCondos: Condo[] = [];
  userTypesLabel = UserTypeLabel;
  condoTypesLabel = CondoTypeLabel;
  defaultStartTime = startOfDay(new Date());
  defaultEndTime = endOfDay(new Date());

  showLinkFields = true;

  constructor(
    private systemBroadcastService: SystemBroadcastService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private whitelabelService: WhiteLabelService,
    private condoService: CondoService
  ) {
    this.systemBroadcastForm
      .get('notificationChannel')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe(value => {
        const isPushOnly = value === 'PUSH';

        if (isPushOnly) {
          this.systemBroadcastForm.get('link')?.reset();
          this.systemBroadcastForm.get('linkButtonName')?.reset();
        }

        this.showLinkFields = !isPushOnly;
      });
  }

  ngOnInit() {
    this.getWhitelabels();
    this.systemBroadcastId = this.activatedRoute.snapshot.params.systemBroadcastId;
    if (this.systemBroadcastId) {
      this.fetch();
    } else {
      this.fetchStatus = 'SUCCESS';
      this.updateFormControlsBasedOnFetchStatus();
    }
  }

  ngOnDestroy() {
    if (this.unsubscribe$) {
      this.unsubscribe$.next(null);
      this.unsubscribe$.complete();
    }
  }

  fetch() {
    if (!this.systemBroadcastId) {
      return;
    }

    this.fetchStatus = 'LOADING';
    this.updateFormControlsBasedOnFetchStatus();

    this.systemBroadcastService
      .getById(this.systemBroadcastId, this.fetchSystemBroadcastQuery)
      .pipe(timeout(20_000), takeUntil(this.unsubscribe$))
      .subscribe({
        next: systemBroadcast => {
          const htmlMessage = this.sanitizer.bypassSecurityTrustHtml(systemBroadcast.htmlMessage);
          this.systemBroadcast = { ...htmlMessage, ...systemBroadcast };
          this.populateForm();
          this.fetchStatus = 'SUCCESS';
          this.updateFormControlsBasedOnFetchStatus();
        },
        error: (err: any) => {
          const errorMessage = err?.originalError?.message || 'Não foi possível baixar os dados do destaque. Tente novamente, por favor.';
          this.notificationService.error('Erro', errorMessage);
          this.fetchStatus = 'ERROR';
          this.updateFormControlsBasedOnFetchStatus();
        }
      });
  }

  get isPushChannel(): boolean {
    return this.systemBroadcastForm.get('notificationChannel')?.value?.includes?.('PUSH');
  }

  updateFormControlsBasedOnFetchStatus(): void {
    const whiteLabels = this.systemBroadcastForm.get('whiteLabels');
    const userTypes = this.systemBroadcastForm.get('userTypes');
    const condoTypes = this.systemBroadcastForm.get('condoTypes');

    const controls = [whiteLabels, userTypes, condoTypes];

    controls.forEach(control => {
      if (!control) return;
      if (this.fetchStatus === 'SUCCESS') {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }

  getWhitelabels() {
    const query: EcondosQuery = {
      $select: 'companyName name',
      $limit: 500
    };
    this.whitelabelService.get(query).subscribe({
      next: whitelabels => {
        this.systemWhiteLabels = whitelabels;
      },
      error: err => {
        const errorMessage = err?.originalError?.message || 'Não foi possível baixar os dados do whiteLabel. Tente novamente, por favor.';
        this.notificationService.error('Erro', errorMessage);
        this.fetchStatus = 'ERROR';
        this.updateFormControlsBasedOnFetchStatus();
      }
    });
  }

  populateForm() {
    this.systemBroadcastForm.patchValue({ ...this.systemBroadcast });
    if (!this.systemBroadcast?.htmlMessage && this.systemBroadcast?.message) {
      this.systemBroadcastForm.controls.htmlMessage.setValue(this.systemBroadcast?.message);
    }
    this.getCondos();
  }

  handleSubmit() {
    if (this.systemBroadcastForm.invalid) {
      this.systemBroadcastForm.markAllAsTouched();
      this.notificationService.warning('Formulário inválido', 'Preencha todos os campos obrigatórios');
      return;
    }
    const message = this.editor?.getContent({ format: 'text' });

    if (!this.systemBroadcastForm.value.condos.length) {
      const list = this.systemCondos.map(condo => condo._id);
      this.systemBroadcastForm.get('condos')?.setValue(list);
    }

    if (!this.systemBroadcastForm.value.condoTypes.length) {
      this.systemBroadcastForm.get('condos')?.setValue([]);
    }

    this.submitFormStatus = 'LOADING';

    const data: Partial<SystemBroadcast> = {
      title: this.systemBroadcastForm.value.title,
      message,
      isActive: this.systemBroadcastForm.value.isActive,
      htmlMessage: this.systemBroadcastForm.value.htmlMessage,
      link: this.systemBroadcastForm.value.link,
      linkButtonName: this.systemBroadcastForm.value.linkButtonName,
      whiteLabels: this.systemBroadcastForm.value.whiteLabels,
      userTypes: this.systemBroadcastForm.value.userTypes,
      notificationChannel: this.systemBroadcastForm.value.notificationChannel,
      condoTypes: this.systemBroadcastForm.value.condoTypes,
      startDate: this.systemBroadcastForm.value.startDate,
      endDate: this.systemBroadcastForm.value.endDate,
      condos: this.systemBroadcastForm.value.condos
    };

    const request$ = this.systemBroadcast
      ? this.systemBroadcastService.update(this.systemBroadcast._id, data)
      : this.systemBroadcastService.create(data);

    request$.pipe(timeout(10_000)).subscribe({
      next: () => {
        this.notificationService.success('Sucesso', 'Aviso cadastrado com sucesso.');
        this.submitFormStatus = 'SUCCESS';
        this.onBack();
      },
      error: err => {
        const errorMessage = err?.originalError?.message || 'Não foi possível criar o aviso. Tente novamente, por favor.';
        this.notificationService.error('Erro', errorMessage);
        this.submitFormStatus = 'ERROR';
      }
    });
  }

  selectAll(selectedValues: string[], formField: string): void {
    const typeControl = this.systemBroadcastForm.get(formField) as UntypedFormControl;
    const lastSelected = selectedValues[selectedValues.length - 1];
    const currentValues = typeControl.value || [];

    if (lastSelected === 'ALL') {
      if (currentValues.length > 1) {
        typeControl.setValue([], { emitEvent: false });
      } else {
        let list;
        switch (formField) {
          case 'condoTypes': {
            list = Object.keys(CondoTypeLabel);
            break;
          }
          case 'userTypes': {
            list = Object.keys(UserTypeLabel);
            break;
          }
          case 'whiteLabels': {
            list = this.systemWhiteLabels.map(whiteLabel => whiteLabel._id);
            break;
          }
          case 'condos': {
            list = this.systemCondos.map(condo => condo._id);
            break;
          }
        }
        typeControl.setValue(list, { emitEvent: false });
      }
    }
    if (['whiteLabels', 'condoTypes'].includes(formField)) {
      this.getCondos();
    }
  }

  getCondos(): void {
    const query: EcondosQuery = {
      $select: 'name type',
      deleted: false,
      $limit: 10000
    };

    const whiteLabelSelected = this.systemBroadcastForm.get('whiteLabels')?.value;
    const condoTypes = this.systemBroadcastForm.get('condoTypes')?.value;

    if (!condoTypes || condoTypes.length === 0) {
      return;
    }

    if (condoTypes.length > 0) {
      query.type = { $in: condoTypes };
    }

    if (whiteLabelSelected?.length > 0) {
      query.whiteLabel = { $in: whiteLabelSelected };
    }

    this.condoService.get(query).subscribe({
      next: condos => {
        this.systemCondos = condos;
        const _condos = this.systemBroadcastForm.get('condos')?.value;
        const newCondos = _condos.filter((condo: any) => this.systemCondos.find(c => c._id === condo));
        this.systemBroadcastForm.get('condos')?.setValue(newCondos);
      },
      error: err => {
        console.log(err);
      }
    });
  }

  onBack() {
    this.router.navigate(['home', 'features', 'system-broadcasts']);
  }
}
